<script lang="ts">
	import { onMount } from "svelte";
	import { goto } from "$app/navigation";
	import { resolve } from "$app/paths";
	import { page } from "$app/state";
	import {
		PUBLIC_ALGOLIA_APP_ID,
		PUBLIC_ALGOLIA_API_KEY,
		PUBLIC_ALGOLIA_INDEX_NAME,
	} from "$app/env/public";
	import { liteClient } from "algoliasearch/lite";
	import { breakpoints as br } from "./utils/getBreakpoints";
	import { searchParamUrl } from "./utils/searchParamUrl";
	import { Button } from "@ishavskraft/shared";

	type HighlightField = { value: string; matchedWords?: string[] };

	// Crawler normalizes records so every hit has `title` (see recordExtractor in Algolia config).
	type AlgoliaHit = {
		objectID: string;
		title?: string;
		headline?: string;
		description?: string;
		content?: string;
		url?: string;
		slug?: string;
		path?: string;
		image?: string;
		imageUrl?: string;
		_highlightResult?: Record<string, HighlightField | undefined>;
		_snippetResult?: Record<string, HighlightField | undefined>;
		// Attached client-side from SEARCH_INDICES, not an Algolia attribute.
		siteOrigin?: string;
	};

	// Indices the dropdown searches, in result order. `siteOrigin` is set for an index covering
	// another site: its records store a path relative to that site, and nothing in the record
	// itself says so, so the index is the only place the origin can come from.
	const SEARCH_INDICES = [
		{
			indexName: PUBLIC_ALGOLIA_INDEX_NAME,
			hitsPerPage: 8,
			siteOrigin: undefined,
		},

		{
			indexName: "faq_pages",
			hitsPerPage: 4,
			siteOrigin: "https://kundeservice.ishavskraft.no",
		},
	];

	const client = liteClient(PUBLIC_ALGOLIA_APP_ID, PUBLIC_ALGOLIA_API_KEY);

	let q = $state("");
	let hits = $state<AlgoliaHit[]>([]);
	let open = $state(false);
	let activeIndex = $state(-1);
	let ctrl: AbortController | undefined;
	let inputEl: HTMLInputElement | undefined = $state();
	let listEl: HTMLUListElement | undefined = $state();
	let containerEl: HTMLDivElement | undefined = $state();
	let debounce: ReturnType<typeof setTimeout>;
	let activeRequest = 0;

	// `replaceState`, not `goto`: the input sits in the header of every (mostly prerendered)
	// page, so a navigation per keystroke-batch would re-run load functions.
	const SEARCH_PARAM = "finn";

	// Own timer, slower than the search: one write per keystroke creeps toward Safari's
	// ~100-per-30s history limit, and the URL must never sit on the path to getting hits.
	const URL_SYNC_DELAY = 600;
	let urlSyncTimer: ReturnType<typeof setTimeout>;

	const syncUrl = (value: string) => {
		const next = searchParamUrl(page.url, SEARCH_PARAM, value);
		if (next === `${page.url.pathname}${page.url.search}`) return;
		try {
			goto(next, { shallow: true, replace: true, state: page.state });
		} catch (error) {
			// A throttled or rejected history write must not take the search down with it.
			console.error("Kunne ikke lagre søket i URL-en", error);
		}
	};

	const queueUrlSync = (value: string) => {
		clearTimeout(urlSyncTimer);
		urlSyncTimer = setTimeout(() => syncUrl(value), URL_SYNC_DELAY);
	};

	const isAbsolute = (href: string): boolean => /^[a-z][a-z0-9+.-]*:|^\/\//i.test(href);

	const linkFor = (hit: AlgoliaHit): string => {
		const href =
			hit.path ??
			hit.url ??
			(hit.slug ? (hit.slug.startsWith("/") ? hit.slug : `/${hit.slug}`) : null);

		if (!href) return "#";
		if (hit.siteOrigin && !isAbsolute(href)) return new URL(href, hit.siteOrigin).href;
		return href;
	};

	const imageFor = (hit: AlgoliaHit): string | null => hit.image ?? hit.imageUrl ?? null;

	const titleMatchCount = (hit: AlgoliaHit): number =>
		hit._highlightResult?.title?.matchedWords?.length ?? 0;

	const runSearch = (query: string) => {
		clearTimeout(debounce);
		if (!query.trim()) {
			ctrl?.abort();
			hits = [];
			open = false;
			activeIndex = -1;
			// Drop the param at once, and cancel any queued write that would put it back.
			clearTimeout(urlSyncTimer);
			syncUrl("");
			return;
		}
		queueUrlSync(query);
		debounce = setTimeout(() => {
			ctrl?.abort();
			ctrl = new AbortController();
			const { signal } = ctrl;
			const requestId = ++activeRequest;

			(async () => {
				try {
					const res = await client.searchForHits<AlgoliaHit>({
						requests: SEARCH_INDICES.map(({ indexName, hitsPerPage }) => ({
							indexName,
							query,
							hitsPerPage,
							attributesToSnippet: ["description:30", "content:30"],
							snippetEllipsisText: "…",
						})),
					});
					if (signal.aborted || requestId !== activeRequest) return;

					hits = res.results
						.flatMap((r, i): AlgoliaHit[] =>
							"hits" in r
								? (r.hits as AlgoliaHit[]).map((hit) => ({
										...hit,
										siteOrigin: SEARCH_INDICES[i]?.siteOrigin,
									}))
								: []
						) // Exclude the front page — its content is too generic to be a useful hit.
						.filter((hit: AlgoliaHit) => linkFor(hit) !== "/") // Algolia ranks each index on its own, so hits from different indices arrive
						// concatenated with no comparable score. How much of the query matched the
						// title is the one signal that means the same thing in both. Sort is stable,
						// so ties keep Algolia's own per-index order.
						.sort((a, b) => titleMatchCount(b) - titleMatchCount(a));
				} catch (err) {
					if ((err as Error).name === "AbortError") return;
					console.error("Algolia search failed", err);
					hits = [];
				} finally {
					if (!signal.aborted && requestId === activeRequest) {
						open = true;
						if (activeIndex < 0 && hits.length) activeIndex = 0;
					}
				}
			})();
		}, 200);
	};

	const onInput = (e: Event) => {
		q = (e.currentTarget as HTMLInputElement).value;
		runSearch(q);
	};

	// The seeded field deliberately does not search — no dropdown covering the page on arrival.
	const onFocus = () => {
		if (q.trim() && !hits.length) runSearch(q);
	};

	// Client-side only — prerendered HTML can't carry the query.
	onMount(() => {
		const initial = page.url.searchParams.get(SEARCH_PARAM)?.trim();
		if (initial) q = initial;
	});

	const pick = (hit: AlgoliaHit) => {
		clearTimeout(debounce);
		// Also drop the queued URL write — it would otherwise land on the page we navigate to.
		clearTimeout(urlSyncTimer);
		ctrl?.abort();
		open = false;
		activeIndex = -1;
		mobileSearchOpen = false;
		const href = linkFor(hit);
		if (href === "#") return;
		// Only a path on this site is a route we can client-side navigate to — resolve() would
		// otherwise reinterpret a foreign-site path as one of ours.
		if (!hit.siteOrigin && hit.path && !isAbsolute(hit.path)) {
			goto(resolve(href));
			return;
		}
		window.location.href = href;
	};

	const scrollActiveIntoView = () => {
		queueMicrotask(() => {
			const el = listEl?.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
			el?.scrollIntoView({ block: "nearest" });
		});
	};

	const onInputKeydown = (e: KeyboardEvent) => {
		if (e.key === "Escape") {
			e.preventDefault();
			if (open) {
				open = false;
				activeIndex = -1;
			} else {
				inputEl?.blur();
				// Below laptop, collapse the input back to the button.
				if (width < laptopPx) mobileSearchOpen = false;
			}
			return;
		}
		if (!open || !hits.length) return;
		if (e.key === "ArrowDown") {
			e.preventDefault();
			activeIndex = (activeIndex + 1) % hits.length;
			scrollActiveIntoView();
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			activeIndex = (activeIndex - 1 + hits.length) % hits.length;
			scrollActiveIntoView();
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (activeIndex >= 0) pick(hits[activeIndex]);
		}
	};

	// ⌘K / Ctrl+K to focus search; pointerdown outside closes the dropdown.
	$effect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				inputEl?.focus();
				inputEl?.select();
			}
		};
		const onPointerDown = (e: PointerEvent) => {
			if (!open) return;
			const target = e.target as Node | null;
			if (target && containerEl && !containerEl.contains(target)) {
				open = false;
				activeIndex = -1;
			}
		};
		window.addEventListener("keydown", onKey);
		window.addEventListener("pointerdown", onPointerDown);
		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener("pointerdown", onPointerDown);
		};
	});

	type HighlightPart = { text: string; match: boolean };

	const decodeEntities = (s: string): string =>
		s
			.replaceAll("&amp;", "&")
			.replaceAll("&lt;", "<")
			.replaceAll("&gt;", ">")
			.replaceAll("&quot;", '"')
			.replaceAll("&#39;", "'");

	// Algolia highlight values are HTML-escaped text with matches wrapped in <em>.
	// Parse into plain-text parts so we can render with real Svelte markup instead of {@html}.
	const splitHighlight = (html: string): HighlightPart[] => {
		const parts: HighlightPart[] = [];
		const re = /<em>([\s\S]*?)<\/em>|([^<]+)/g;
		let m: RegExpExecArray | null;

		while ((m = re.exec(html))) {
			if (m[1] !== undefined) parts.push({ text: decodeEntities(m[1]), match: true });
			else if (m[2]) parts.push({ text: decodeEntities(m[2]), match: false });
		}
		return parts;
	};

	const titlePartsFor = (hit: AlgoliaHit): HighlightPart[] =>
		splitHighlight(
			hit._highlightResult?.title?.value ?? hit.title ?? hit.headline ?? "(uten tittel)"
		);

	const descriptionPartsFor = (hit: AlgoliaHit): HighlightPart[] => {
		const snippet = hit._snippetResult?.description?.value ?? hit._snippetResult?.content?.value;
		if (snippet) return splitHighlight(snippet);
		const raw = hit.description ?? hit.content ?? "";
		if (!raw) return [];
		const truncated = raw.length > 200 ? `${raw.slice(0, 200).trim()}…` : raw;
		return [{ text: truncated, match: false }];
	};

	// Below laptop the header row has no room for the full input beside the nav links +
	// Min side + Meny, so it collapses to a search-icon button that expands on tap. The
	// collapse/expand is driven by CSS (see the style block below) rather than this width, so the server
	// and client render the same markup — toggling it in JS off `width` caused the SSR input
	// to swap to the button after hydration (a layout shift / CLS regression).
	const laptopPx = +br.laptop.replace("px", "");
	let width = $state(typeof window === "undefined" ? Infinity : window.innerWidth);
	let mobileSearchOpen = $state(false);

	$effect(() => {
		if (mobileSearchOpen) {
			inputEl?.focus();
			inputEl?.select();
		}
	});
</script>

<svelte:window bind:innerWidth={width} />
<div class="search-root" class:open={mobileSearchOpen}>
	<div class="search-toggle">
		<Button
			aria-label="Åpne søk"
			onclick={() => (mobileSearchOpen = true)}
			class="rounded-full p-2 bg-white grid place-items-center"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="18"
				height="18"
				viewBox="0 0 18 18"
				fill="#002987"
				aria-hidden="true"
			>
				<path
					d="M6.5 13C4.68333 13 3.146 12.3707 1.888 11.112C0.63 9.85333 0.000667196 8.316 5.29101e-07 6.5C-0.000666138 4.684 0.628667 3.14667 1.888 1.888C3.14733 0.629333 4.68467 0 6.5 0C8.31533 0 9.853 0.629333 11.113 1.888C12.373 3.14667 13.002 4.684 13 6.5C13 7.23333 12.8833 7.925 12.65 8.575C12.4167 9.225 12.1 9.8 11.7 10.3L17.3 15.9C17.4833 16.0833 17.575 16.3167 17.575 16.6C17.575 16.8833 17.4833 17.1167 17.3 17.3C17.1167 17.4833 16.8833 17.575 16.6 17.575C16.3167 17.575 16.0833 17.4833 15.9 17.3L10.3 11.7C9.8 12.1 9.225 12.4167 8.575 12.65C7.925 12.8833 7.23333 13 6.5 13ZM6.5 11C7.75 11 8.81267 10.5627 9.688 9.688C10.5633 8.81333 11.0007 7.75067 11 6.5C10.9993 5.24933 10.562 4.187 9.688 3.313C8.814 2.439 7.75133 2.00133 6.5 2C5.24867 1.99867 4.18633 2.43633 3.313 3.313C2.43967 4.18967 2.002 5.252 2 6.5C1.998 7.748 2.43567 8.81067 3.313 9.688C4.19033 10.5653 5.25267 11.0027 6.5 11Z"
					fill="#002987"
				></path>
			</svg>
		</Button>
	</div>
	<div
		bind:this={containerEl}
		class="search-expanded relative"
		onfocusout={(e) => {
			if (!containerEl?.contains(e.relatedTarget as Node)) {
				mobileSearchOpen = false;
				activeIndex = -1;
			}
		}}
	>
		<label class="block relative">
			<span class="sr-only">Søkeord</span>
			<input
				bind:this={inputEl}
				value={q}
				oninput={onInput}
				onfocus={onFocus}
				onkeydown={onInputKeydown}
				type="search"
				autocomplete="off"
				class="search-input text-input w-full"
				placeholder="Søk etter innhold…"
				role="combobox"
				aria-expanded={open}
				aria-controls="search-results"
				aria-autocomplete="list"
				aria-activedescendant={activeIndex >= 0 ? `search-opt-${activeIndex}` : undefined}
			/>
		</label>

		{#if open}
			<ul
				bind:this={listEl}
				id="search-results"
				role="listbox"
				class="search-hits list-none bg-white max-h-96 overflow-auto shadow-lg z-10 rounded-lg mt-1 pt-1 px-2 {width <
				laptopPx
					? 'fixed left-0 right-0 top-[64px] w-full max-w-none'
					: 'absolute top-full right-0 w-[480px] max-w-[90vw] mt-1'}"
			>
				{#if hits.length === 0}
					<li class="px-3 py-2 text-sm text-gray-500">Ingen treff for «{q}»</li>
				{:else}
					{#each hits as hit, i (hit.objectID)}
						{@const titleParts = titlePartsFor(hit)}
						{@const descParts = descriptionPartsFor(hit)}
						{@const href = linkFor(hit)}
						{@const image = imageFor(hit)}
						<li class="mb-2">
							<button
								id="search-opt-{i}"
								data-idx={i}
								type="button"
								role="option"
								aria-selected={i === activeIndex}
								class="w-full h-auto flex items-start gap-3 text-left px-3 py-2 rounded-md text-sm border-0 {i ===
								activeIndex
									? 'bg-gray-200'
									: 'bg-transparent hover:bg-gray-100'}"
								onmousemove={() => (activeIndex = i)}
								onpointerdown={(e) => e.preventDefault()}
								onclick={() => pick(hit)}
							>
								{#if image}
									<img
										src={image}
										alt=""
										loading="lazy"
										class="w-12 h-12 object-cover rounded shrink-0"
									/>
								{/if}
								<span class="min-w-0 flex-1 flex flex-col">
									<span class="font-semibold truncate">
										{#each titleParts as part, j (j)}
											{#if part.match}<em>{part.text}</em>{:else}{part.text}{/if}
										{/each}
									</span>
									{#if descParts.length}
										<span class="text-gray-600 truncate">
											{#each descParts as part, j (j)}
												{#if part.match}<em>{part.text}</em>{:else}{part.text}{/if}
											{/each}
										</span>
									{/if}
									<span class="text-gray-500 text-xs truncate">{href}</span>
								</span>
							</button>
						</li>
					{/each}
				{/if}
			</ul>
		{/if}
	</div>
</div>

<style lang="postcss">
	.search-root {
		display: contents;
	}

	.search-expanded {
		display: none;
	}

	.search-root.open .search-toggle {
		display: none;
	}

	.search-root.open .search-expanded {
		display: block;
	}

	@media (--laptop) {
		.search-toggle {
			display: none;
		}

		.search-expanded {
			display: block;
		}
	}

	.search-hits :global(em) {
		font-style: normal;
		font-weight: 600;
		background-color: color-mix(in srgb, currentColor 12%, transparent);
	}
</style>
